<template>
    <vx-filterable :active="active" @toggle="toggleFilterable">
        <template v-slot:placeholder>
            <span class="mr-2">Search</span>
            <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                {{ pluralized('filter', filtersCount, true )}}
                <font-awesome-icon widthAuto :icon="faCircleXmark" />
            </vx-badge>
        </template>
        <template v-slot:form>
            <form @submit.prevent="apply" class="space-y-4">
                <div class="space-y-1">
                    <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                        Keyword Search
                    </filter-label>

                    <vx-input
                        type="text"
                        data-test="filters-query"
                        placeholder="Search"
                        maxlength="120"
                        v-model="filters.query" size="lg">
                        <template v-slot:right>
                            <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                        </template>
                    </vx-input>
                </div>

                <div v-if="FEATURES.locked_snippets && (!onCorporate() || props.templateView)" class="space-y-1" data-test="type-filter">
                    <filter-label
                        :canReset="filters['filter[is_locked]'] !== undefined"
                        @reset="() => { filters['filter[is_locked]'] = undefined; }"
                    >
                        Type
                    </filter-label>

                    <div class="w-full flex items-center justify-between gap-1">
                        <vx-checkbox-selection-item
                            @selected="lockedFilter = { ...lockedFilter, regular: !lockedFilter.regular }"
                            :active="lockedFilter.regular"
                            class="grow"
                        >
                            Regular
                        </vx-checkbox-selection-item>
                        <vx-checkbox-selection-item
                            @selected="lockedFilter = { ...lockedFilter, locked: !lockedFilter.locked }"
                            :active="lockedFilter.locked"
                            class="grow"
                        >
                            Locked
                        </vx-checkbox-selection-item>
                    </div>
                </div>

                <div class="flex gap-2 pt-4">
                    <vx-button type="button" color="muted-light" size="lg" @click="cancel">Cancel</vx-button>
                    <vx-button data-test="filters-apply" type="submit" size="lg" block>
                        Apply
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </vx-button>
                </div>
            </form>
        </template>
    </vx-filterable>
</template>

<script setup>
import { computed, inject, ref, watch, nextTick } from 'vue';
import { VxBadge, VxButton, VxCheckboxSelectionItem, VxFilterable, VxInput } from '@voxie/frontend-components';
import { faCircleCheck, faMagnifyingGlass, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const FEATURES = inject('FEATURES');
const { onCorporate } = useCurrentGroup();

const filters = ref({})
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});
watch(() => props.modelValue, (modelValue) => {
    filters.value = {...modelValue};
}, {
    deep: true,
    immediate: true,
})
const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
}

const lockedFilter = computed({
    get() {
        const value = filters.value['filter[is_locked]'];
        return {
            locked: value === 1 || value === null,
            regular: value === 0 || value === null,
        };
    },
    set({ locked, regular }) {
        if (locked && !regular) {
            filters.value['filter[is_locked]'] = 1;
        } else if (!locked && regular) {
            filters.value['filter[is_locked]'] = 0;
        } else if (locked && regular) {
            filters.value['filter[is_locked]'] = null;
        } else {
            filters.value['filter[is_locked]'] = undefined;
        }
    },
});

const filtersCount = computed(
    () =>
        (props.modelValue.query ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue['filter[is_locked]']) ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
}
const cancel = async () => {
    filters.value = { ...props.modelValue }
    active.value = false;
}

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
}

</script>
